import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, FileText, TrendingUp, DollarSign, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  const pricingPlans = [
    {
      icon: <Calendar size={40} />,
      title: 'Monthly Returns',
      price: '1,500',
      period: 'per month',
      description: 'Complete monthly return filing services',
      features: [
        'GSTR-1 Filing',
        'GSTR-3B Filing',
        'TDS Return Filing',
        'Monthly Compliance',
        'Expert Support',
        'Timely Reminders',
      ],
      color: 'bg-[#2D3C92]',
      popular: false,
    },
    {
      icon: <FileText size={40} />,
      title: 'ITR Filing',
      price: '2,500',
      period: 'per return',
      description: 'Professional income tax return filing',
      features: [
        'All ITR Forms',
        'Tax Calculation',
        'Maximum Deductions',
        'E-Verification',
        'Refund Tracking',
        'Expert Consultation',
      ],
      color: 'bg-[#10B981]',
      popular: true,
    },
    {
      icon: <TrendingUp size={40} />,
      title: 'EPF Return Filing',
      price: '2,500',
      period: 'per return',
      description: 'EPF compliance and return filing',
      features: [
        'EPF Return Filing',
        'ECR Preparation',
        'Challan Generation',
        'Compliance Check',
        'Error Resolution',
        'Monthly Support',
      ],
      color: 'bg-[#2D3C92]',
      popular: false,
    },
    {
      icon: <DollarSign size={40} />,
      title: 'TDS Filing',
      price: '1,500',
      period: 'per quarter',
      description: 'Quarterly TDS return filing',
      features: [
        'Form 24Q, 26Q, 27Q',
        'TDS Calculation',
        'Challan Verification',
        'Form 16/16A',
        'Trace Updates',
        'Penalty Avoidance',
      ],
      color: 'bg-[#10B981]',
      popular: false,
    },
  ];

  return (
    <div className="pt-32 pb-20" data-testid="pricing-page">
      {/* Hero Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 tracking-tight" data-testid="pricing-hero-title">
              Service Pricing & Plans
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Transparent pricing for all our tax and compliance services. Choose the plan that fits your needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white" data-testid="pricing-cards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className={`relative bg-white rounded-2xl p-8 border-2 ${
                  plan.popular ? 'border-[#10B981] shadow-2xl' : 'border-slate-200 shadow-lg'
                } hover:shadow-2xl transition-all`}
                data-testid={`pricing-card-${index}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#10B981] text-white px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className={`${plan.color} w-16 h-16 rounded-xl flex items-center justify-center text-white mb-6 floating`}>
                  {plan.icon}
                </div>

                <h3 className="text-2xl font-bold text-[#0F172A] mb-2">{plan.title}</h3>
                <p className="text-slate-600 text-sm mb-6">{plan.description}</p>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#0F172A]">₹{plan.price}</span>
                  <span className="text-slate-600 text-sm ml-2">/ {plan.period}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="text-[#10B981] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-slate-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`block w-full text-center py-3 rounded-full font-semibold transition-all ${
                    plan.popular
                      ? 'bg-[#10B981] text-white hover:bg-[#047857]'
                      : 'border-2 border-[#2D3C92] text-[#2D3C92] hover:bg-[#2D3C92] hover:text-white'
                  }`}
                  data-testid={`pricing-cta-${index}`}
                >
                  Get Started
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="additional-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">Other Services</h2>
            <p className="text-lg text-slate-600">We offer comprehensive tax services tailored to your needs</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'GST Registration', price: '₹3,000', link: '/gst-registration' },
              { name: 'GST Annual Return', price: '₹5,000', link: '/gst-services' },
              { name: 'TAN Registration', price: '₹1,500', link: '/income-tax-services' },
              { name: 'Tax Planning', price: '₹10,000', link: '/contact' },
              { name: 'Business Tax Return', price: '₹5,000', link: '/income-tax-services' },
              { name: 'Tally Maintenance', price: '₹500/month', link: '/contact' },
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-all"
                data-testid={`other-service-${index}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-[#0F172A] mb-1">{service.name}</h4>
                    <p className="text-2xl font-bold text-[#10B981]">{service.price}</p>
                  </div>
                  <Link
                    to={service.link}
                    className="text-[#2D3C92] hover:text-[#10B981] transition-colors font-medium"
                  >
                    Learn More →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#2D3C92] to-[#10B981]" data-testid="pricing-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Need a Custom Plan?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              We offer customized pricing for businesses with unique requirements. Contact us for a tailored solution.
            </p>
            <Link
              to="/contact"
              className="bg-white text-[#2D3C92] hover:bg-slate-100 px-8 py-4 rounded-full font-bold text-lg inline-block transition-all shadow-xl"
              data-testid="custom-plan-cta"
            >
              Contact Us for Custom Pricing
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;